package com.hyperether.goodjob.scenes.map

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.material.Button
import androidx.compose.material.ButtonDefaults
import androidx.compose.material.Divider
import androidx.compose.material.Icon
import androidx.compose.material.Text
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.text.font.FontWeight
import androidx.navigation.NavHostController
import com.hyperether.goodjob.log
import com.hyperether.goodjob.map.MapComponent
import com.hyperether.goodjob.navigation.AddNewJob
import com.hyperether.goodjob.repository.prefs.PrefsManager
import com.hyperether.goodjob.repository.remote.model.LatLng
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.add
import com.hyperether.goodjob.resources.places
import com.hyperether.goodjob.scenes.components.SearchMapWeb
import com.hyperether.goodjob.scenes.components.WebViewHeaderWithRightContent
import com.hyperether.goodjob.scenes.components.disableMouseDragScrollOnWeb
import com.hyperether.goodjob.scenes.components.verticalScrollbar
import com.hyperether.goodjob.scenes.jobs.components.FilterJobsDialogWeb
import com.hyperether.goodjob.scenes.jobs.components.JobsItem
import com.hyperether.goodjob.scenes.register.FullScreenProgressBar
import com.hyperether.goodjob.theme.BlueApp
import com.hyperether.goodjob.theme.BorderColor
import com.hyperether.goodjob.util.FilterDialogStateManager
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp
import com.hyperether.resources.stringResource
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

@Composable
fun MapScreenWeb(
    viewModel: MapViewModel,
    prefsManager: PrefsManager,
    navHostController: NavHostController
) {
    val scope = rememberCoroutineScope()
    val showLoader = remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        viewModel.getUserPosition()
        viewModel.init()
    }
    val searchText = remember { mutableStateOf("") }
    Column {
        WebViewHeaderWithRightContent(
            isBackAvailable = false,
            title = stringResource(Res.string.places)
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                SearchMapWeb(searchText)
                Spacer(modifier = Modifier.width(12.toScaleDp()))
                if (!viewModel.isEmployee()) {
                    Button(
                        modifier = Modifier
                            .height(35.toScaleDp())
                            .widthIn(min = 85.toScaleDp())
                            .pointerHoverIcon(PointerIcon.Hand),
                        onClick = {
                            navHostController.navigate(AddNewJob(""))
                        },
                        colors = ButtonDefaults.buttonColors(BlueApp),
                        shape = RoundedCornerShape(32.toScaleDp())
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.Center
                        ) {
                            DisableSelection {
                                Text(
                                    text = stringResource(Res.string.add),
                                    color = Color.White,
                                    fontSize = 16.toScaleSp(),
                                    fontWeight = FontWeight.W400
                                )
                            }
                            Spacer(modifier = Modifier.width(8.toScaleDp()))
                            Icon(
                                imageVector = Icons.Default.Add,
                                contentDescription = stringResource(Res.string.add),
                                tint = Color.White,
                                modifier = Modifier.size(16.toScaleDp())
                            )
                        }
                    }
                }
            }
        }

        log.d("MAP") { "MapScreenWeb filteredJobsSearch" }
        val filteredJobsSearch by remember(searchText.value) {
            derivedStateOf { viewModel.searchMapJobs(searchText.value) }
        }
        val userTeams = viewModel.getUser()?.teams
        Row {
            if (viewModel.jobList.isNotEmpty()) {
                FilterJobsDialogWeb(
                    modifier = Modifier.weight(0.5f),
                    viewModel.getFilterMap(),
                    onApply = { map ->
                        FilterDialogStateManager.setDialogShowing(false)
                        viewModel.applyFilter(map)
                        viewModel.filterJobs()
                    },
                    isEmployee = viewModel.isEmployee(),
                    userTeams = userTeams
                )
            }
            if (filteredJobsSearch.isNotEmpty()) {
                val sortedJobList = viewModel.getSortedJobList(filteredJobsSearch)
                if (sortedJobList.isNotEmpty()) {
                    if (viewModel.selectedJob.value == null) {
                        viewModel.selectedJob.value = sortedJobList.firstOrNull()
                    }

                    Divider(
                        modifier = Modifier
                            .width(1.toScaleDp())
                            .fillMaxHeight()
                            .background(BorderColor)
                    )
                    val listState = rememberLazyListState()
                    LazyColumn(
                        state = listState,
                        modifier = Modifier
                            .fillMaxHeight()
                            .weight(0.5f)
                            .verticalScrollbar(listState)
                            .disableMouseDragScrollOnWeb(),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        contentPadding = PaddingValues(
                            start = 12.toScaleDp(),
                            top = 12.toScaleDp(),
                            end = 18.toScaleDp(),
                            bottom = 12.toScaleDp()
                        )
                    ) {
                        itemsIndexed(sortedJobList) { index, job ->
                            val isLast = index == sortedJobList.lastIndex
                            JobsItem(
                                site = viewModel.sitesMap[job.contactSiteId],
                                job = job,
                                isInEditMode = false,
                                selectedJobs = mutableListOf(),
                                onJobSelected = { _, _ -> },
                                onItemClick = {
                                    scope.launch {
                                        showLoader.value = true
                                        delay(1)
                                        viewModel.sitesMap[job.contactSiteId]?.addressObject?.let { addressObject ->
                                            addressObject.lat?.let { lat ->
                                                addressObject.long?.let { lon ->
                                                    viewModel.selectedJob.value = job
                                                    viewModel.centerPosition.value =
                                                        LatLng(lat, lon)
                                                    CoroutineScope(Dispatchers.Default).launch {
                                                        viewModel.mapZoom.value = null
                                                        delay(200)
                                                        viewModel.mapZoom.value = true
                                                    }

                                                }
                                            }
                                        }
                                        delay(200)
                                        showLoader.value = false
                                    }
                                },
                                isMapScreen = true
                            )
                            if (!isLast) {
                                Spacer(modifier = Modifier.height(12f.toScaleDp()))
                            }
                        }
                    }
                }
            }

            val mapWeight = if (viewModel.filteredJobs.isNotEmpty()) 1f else 1.5f
            Box(modifier = Modifier.weight(mapWeight).fillMaxHeight()) {
                MapComponent(
                    showMapWeb = mutableStateOf(true),
                    showMap = true,
                    markers = viewModel.markers,
                    currentPosition = viewModel.centerPosition,
                    mapZoom = viewModel.mapZoom
                ) { lat, lon ->
                    viewModel.centerPosition.value = LatLng(lat, lon)
                }
            }

        }
        if (showLoader.value) {
            FullScreenProgressBar()
        }
    }
}